<template>
    <primevue-button
        class="selection-option-card"
        :class="{ selected: props.selected, 'no-icon': !hasLeadingVisual }"
        :disabled="props.disabled"
        @click="emit('click')"
    >
        <country-flag
            v-if="props.country"
            class="card-icon country-flag"
            :iso-code="props.country"
        />
        <icofont-icon
            v-else-if="props.icofontIcon"
            class="card-icon"
            :icon="props.icofontIcon"
            :icon-colour="props.iconTextColor ?? undefined"
            :background-colour="props.iconBackgroundColor ?? undefined"
            size="large"
        />
        <div v-else-if="props.icofontIcons?.length" class="card-icons">
            <icofont-icon
                v-for="icon in props.icofontIcons"
                :key="icon.icon"
                :icon="icon.icon"
                :icon-colour="icon.iconTextColor ?? undefined"
                :background-colour="icon.backgroundColour ?? undefined"
                size="large"
            />
        </div>
        <div
            v-else-if="props.faIcon"
            class="card-icon font-awesome-icon-wrapper"
            :style="iconStyle"
        >
            <font-awesome-icon
                class="font-awesome-icon"
                :icon="props.faIcon"
            />
        </div>
        <div class="title-row">
            <h4>{{ props.title }}</h4>
            <slot name="top-right" />
        </div>
        <p v-if="props.description" class="description" v-html="props.description" />
        <div v-if="$slots.footer" class="footer-slot">
            <slot name="footer" />
        </div>
    </primevue-button>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import PrimevueButton from 'primevue/button';
import CountryFlag from '@/components/powerMarkets/shared/CountryFlag.vue';
import IcofontIcon from '@/components/powerMarkets/common/IcofontIcon.vue';
import type { FontAwesomeIconValue } from '@/types/general/FontAwesomeIconValues.ts';

const props = withDefaults(defineProps<{
    title: string;
    country?: string | null;
    icofontIcon?: string | null;
    icofontIcons?: Array<{icon: string; backgroundColour?: string | null; iconTextColor?: string | null}> | null;
    faIcon?: FontAwesomeIconValue | null;
    iconBackgroundColor?: string | null;
    iconTextColor?: string | null;
    description?: string | null;
    selected?: boolean;
    disabled?: boolean;
}>(), {
    country: null,
    icofontIcon: null,
    icofontIcons: null,
    faIcon: null,
    iconBackgroundColor: null,
    iconTextColor: null,
    description: null,
    selected: false,
    disabled: false,
});

const emit = defineEmits<{ click: [] }>();

const hasLeadingVisual = computed<boolean>(() => !!props.country
    || !!props.icofontIcon
    || !!props.icofontIcons?.length
    || !!props.faIcon);

const iconStyle = computed<Record<string, string>>(() => {
    const style: Record<string, string> = {};
    if (props.iconBackgroundColor) {
        style.backgroundColor = props.iconBackgroundColor;
    }
    if (props.iconTextColor) {
        style.color = props.iconTextColor;
    }
    return style;
});
</script>

<style lang="scss" scoped>
.selection-option-card {
    position: relative;
    display: grid;
    grid-template-columns: auto 1fr;
    align-items: start;
    gap: var(--largeSpacing);
    width: 100%;
    height: 100%;
    padding: var(--defaultSpacing);
    border-radius: var(--borderRadiusOuter);
    border: solid 1px var(--neutral400);
    background-color: var(--neutral200);
    color: var(--defaultFont);
    font-size: var(--smallFontSize);
    text-align: left;
    transition: all 300ms;
    justify-items: start;

    &:hover {
        background-color: var(--neutral300);
        border-color: var(--neutral500);
    }

    &.selected {
        border-color: var(--accentColour);
        background-color: var(--neutral300);
    }

    &.no-icon {
        grid-template-columns: 1fr;
    }

    :deep(.p-button-label) {
        display: contents;
    }

    .card-icon {
        grid-column: 1;
        align-self: center;
    }

    .card-icons {
        grid-column: 1;
        display: flex;
        align-self: center;
        gap: var(--smallSpacing);
    }

    .country-flag {
        width: 6rem;
        height: 4rem;
    }

    .font-awesome-icon-wrapper {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: var(--smallSpacing);
        border-radius: var(--borderRadiusOuter);
    }

    .font-awesome-icon {
        font-size: var(--largeFontSize);
    }

    .title-row {
        grid-column: 2;
        display: flex;
        align-items: center;
        align-self: center;
        justify-content: space-between;
        gap: var(--defaultSpacing);
        width: 100%;
    }

    .description {
        grid-column: 1 / -1;
        margin: 0;
        color: var(--neutral900);
    }

    .footer-slot {
        grid-column: 1 / -1;
        width: 100%;
    }
}
</style>
